<template>
  <div class="tf">
    <Breadcrumbs main="Команды" :title="team.name || 'Команда'"/>

    <!-- ======================= модалки ======================= -->
    <b-modal centered title="Переименовать команду" v-model="isShowRenameModal" hide-footer>
      <label class="tf-label">Новое название</label>
      <b-form-input v-model="newTeamName" :state="newTeamName ? isRenameValid : null"
                    placeholder="Название команды" @keyup.enter="confirmRename"/>
      <div class="tf-hint">Минимум 2 символа.</div>
      <div v-if="renameError" class="tf-err">{{ renameError }}</div>
      <div class="d-flex justify-content-end mt-3">
        <button type="button" class="tf-btn-ghost mr-2" :disabled="isRenaming"
                @click="isShowRenameModal = false">Отмена
        </button>
        <button type="button" class="tf-btn-primary" :disabled="isRenaming || !isRenameValid"
                @click="confirmRename">
          <i class="fa" :class="isRenaming ? 'fa-spinner tf-spin' : 'fa-check'"></i>
          {{ isRenaming ? 'Сохраняем…' : 'Сохранить' }}
        </button>
      </div>
    </b-modal>

    <b-modal centered title="Удаление команды" v-model="isShowDeleteTeamModal" hide-footer>
      <p>Вы действительно хотите удалить команду <strong>{{ team.name }}</strong>?</p>
      <div v-if="deleteTeamError" class="tf-err">{{ deleteTeamError }}</div>
      <div class="d-flex justify-content-end mt-3">
        <button type="button" class="tf-btn-ghost mr-2" :disabled="isDeletingTeam"
                @click="isShowDeleteTeamModal = false">Отмена
        </button>
        <button type="button" class="tf-btn-primary ui-btn-danger-solid" :disabled="isDeletingTeam"
                @click="confirmDeleteTeam">
          <i class="fa" :class="isDeletingTeam ? 'fa-spinner tf-spin' : 'fa-trash-o'"></i>
          {{ isDeletingTeam ? 'Удаляем…' : 'Удалить' }}
        </button>
      </div>
    </b-modal>

    <b-modal centered :title="`Рейтинг команды ${team.name || ''}`" v-model="show" hide-footer>
      <label class="tf-label">Рейтинг</label>
      <b-form-input type="number" v-model="newRating"/>
      <label class="tf-label mt-3">Описание</label>
      <b-form-textarea v-model="newDescription" rows="3"/>
      <div class="d-flex justify-content-end mt-3">
        <button type="button" class="tf-btn-ghost mr-2" @click="show = false">Отмена</button>
        <button type="button" class="tf-btn-primary" @click="acceptTeamRating">
          <i class="fa fa-check"></i> Добавить
        </button>
      </div>
    </b-modal>

    <b-modal id="modal-edit-avatar" title="Логотип команды" hide-footer centered>
      <div class="ui-logo-modal">
        <img :src="logoUrl" class="ui-logo-modal__img"
             @error="event => event.target.src = TeamImagePug"/>
      </div>
      <button type="button" class="tf-btn-primary w-100 mt-3" @click="handleLogoClick">
        <i class="fa fa-upload"></i> Загрузить новый
      </button>
      <input ref="logoInput" type="file" accept="image/*" style="display: none;" @change="handleLogoSelect">
      <button v-if="hasLogo" type="button" class="tf-btn-ghost w-100 mt-2"
              @click="handleDownloadTeamAvatar(team.logo.original_url, `${team.name}-avatar`)">
        <i class="fa fa-download"></i> Скачать
      </button>
      <button v-if="hasLogo" type="button" class="tf-btn-ghost ui-btn-danger w-100 mt-2" @click="deleteLogo">
        <i class="fa fa-trash-o"></i> Удалить логотип
      </button>
    </b-modal>

    <div class="container-fluid tf-wrap">

      <!-- ======================= верхняя панель ======================= -->
      <div class="tf-topbar">
        <button type="button" class="tf-back" @click="goBack" v-b-tooltip.hover.bottom="'К списку команд'">
          <i class="fa fa-arrow-left"></i>
        </button>

        <div class="tf-topbar__title">
          <div class="tf-topbar__name" :class="{ 'is-placeholder': !team.name }">
            {{ team.name || 'Команда' }}
          </div>
          <div class="tf-topbar__meta">
            <button type="button" class="ui-copy" @click="copyValue(team.id, 'ID')"
                    v-b-tooltip.hover.bottom="'Скопировать ID'">
              ID {{ team.id || '—' }} <i class="fa fa-copy"></i>
            </button>
            <i class="tf-dot"></i>
            <span>{{ (team.game && team.game.name) || 'дисциплина не указана' }}</span>
            <i class="tf-dot"></i>
            <span>{{ (members || []).length }} участников</span>
          </div>
        </div>

        <div class="tf-topbar__spacer"></div>

        <div class="tf-topbar__actions">
          <router-link :to="{ name: 'teams_add_player', params: { id: $route.params.id } }" class="tf-btn-ghost">
            <i class="fa fa-user-plus"></i> Добавить игрока
          </router-link>
          <button type="button" class="tf-icon-btn" @click="openRenameModal"
                  v-b-tooltip.hover.bottom="'Переименовать'">
            <i class="fa fa-pencil"></i>
          </button>
          <button type="button" class="tf-icon-btn ui-icon-danger" @click="openDeleteTeamModal"
                  v-b-tooltip.hover.bottom="'Удалить команду'">
            <i class="fa fa-trash-o"></i>
          </button>
        </div>
      </div>

      <!-- ======================= табы ======================= -->
      <div class="ui-tabs" ref="tabsTop">
        <button v-for="tab in tabs" :key="tab.key" type="button"
                class="ui-tab" :class="{ 'is-on': activeTab === tab.key }"
                @click="goTab(tab.key)">
          <i class="fa" :class="tab.icon"></i>
          <span>{{ tab.label }}</span>
          <span v-if="tab.count !== null" class="ui-count">{{ tab.count }}</span>
        </button>
      </div>

      <div class="ui-layout">
        <div class="ui-main">

          <!-- ============ ОБЩЕЕ ============ -->
          <section v-show="activeTab === 'main'" class="tf-sec">

            <div v-if="bans.length" class="tf-alert mb-3">
              <i class="fa fa-ban tf-alert__icon"></i>
              <div class="tf-alert__body">
                <div class="tf-alert__title">Активных банов: {{ bans.length }}</div>
                <div v-for="ban in bans" :key="'ban' + ban.id" class="ui-ban">
                  <b>{{ ban.game_name || 'Команда' }}</b>
                  <span>с {{ ban.from || '—' }} до {{ ban.to || '—' }}</span>
                  <em v-if="ban.description">{{ ban.description }}</em>
                </div>
              </div>
            </div>

            <div v-if="team.deleted_at" class="tf-alert mb-3">
              <i class="fa fa-archive tf-alert__icon"></i>
              <div class="tf-alert__body">
                <div class="tf-alert__title">Команда удалена {{ formatDateTime(team.deleted_at) }}</div>
              </div>
            </div>

            <div class="tf-card">
              <div class="tf-card__head">
                <span class="tf-card__icon"><i class="fa fa-info"></i></span>
                <div class="tf-card__titles">
                  <h3 class="tf-card__title">О команде</h3>
                  <p class="tf-card__sub">Данные из профиля команды</p>
                </div>
              </div>
              <div class="tf-card__body">
                <dl class="ui-dl">
                  <div>
                    <dt>Создатель</dt>
                    <dd>{{ owner || '—' }}</dd>
                  </div>
                  <div>
                    <dt>Дисциплина</dt>
                    <dd>{{ (team.game && team.game.name) || '—' }}</dd>
                  </div>
                  <div>
                    <dt>Страна</dt>
                    <dd>{{ (team.country && team.country.name) || '—' }}</dd>
                  </div>
                  <div>
                    <dt>Дивизион</dt>
                    <dd>{{ (team.division && team.division.name) || '—' }}</dd>
                  </div>
                  <div>
                    <dt>Рейтинг</dt>
                    <dd>{{ team.rating !== null && team.rating !== undefined ? team.rating : '—' }}</dd>
                  </div>
                  <div>
                    <dt>Всего турниров</dt>
                    <dd>{{ team.tournaments_count || 0 }}</dd>
                  </div>
                  <div>
                    <dt>Создана</dt>
                    <dd>{{ formatDateTime(team.created_at) }}</dd>
                  </div>
                </dl>
              </div>
            </div>

            <!-- Pro Division -->
            <div class="tf-card">
              <div class="tf-card__head">
                <span class="tf-card__icon"><i class="fa fa-star"></i></span>
                <div class="tf-card__titles">
                  <h3 class="tf-card__title">Pro Division</h3>
                  <p class="tf-card__sub">Профессиональный статус для элитных турниров</p>
                </div>
                <span class="tf-badge" :class="isProDivision ? 'tf-badge--ok' : ''">
                  {{ isProDivision ? 'Активно' : 'Неактивно' }}
                </span>
              </div>
              <div class="tf-card__body">
                <div class="tf-toggle" :class="{ 'is-on': isProDivision }">
                  <b-form-checkbox v-model="isProDivision" switch
                                   :disabled="isLoadingToggleProDivision" @change="toggleProDivision">
                    <span class="tf-toggle__title">
                      Pro Division
                      <b-spinner small v-if="isLoadingToggleProDivision" class="ml-2"></b-spinner>
                    </span>
                    <span class="tf-toggle__text">Команда участвует в профессиональных турнирах</span>
                  </b-form-checkbox>
                </div>

                <div v-if="isProDivision" class="ui-region">
                  <div class="tf-label">Регион</div>
                  <div class="ui-region__row">
                    <b-form-select :disabled="!!selectedProRegionId" v-model="selectedProRegionId"
                                   :options="regionsOptions"/>
                    <button type="button" class="tf-btn-ghost" :disabled="!selectedProRegionId"
                            @click="detachProTeamRegion(team.id)">
                      Отвязать
                    </button>
                  </div>
                  <div class="tf-hint">Регион привязывается один раз — чтобы сменить, сначала отвяжите текущий.</div>
                </div>
              </div>
            </div>

            <!-- Участники -->
            <div class="tf-card">
              <div class="tf-card__head">
                <span class="tf-card__icon"><i class="fa fa-users"></i></span>
                <div class="tf-card__titles">
                  <h3 class="tf-card__title">Участники</h3>
                  <p class="tf-card__sub">Состав команды и игровые профили</p>
                </div>
                <span class="tf-badge">{{ (members || []).length }}</span>
                <router-link :to="{ name: 'teams_add_player', params: { id: $route.params.id } }" class="tf-link">
                  <i class="fa fa-user-plus"></i> Добавить
                </router-link>
              </div>
              <div class="tf-card__body ui-players">
                <PubgPlayerTable v-if="team.game && team.game.slug === 'pubg-mobile'"
                                 :tr-class="trClass" :teamMembers="members"/>
                <MlbbPlayerTable v-else-if="team.game && team.game.slug === 'mlbb'"
                                 :tr-class="trClass" :teamMembers="members"/>
                <div v-else class="ui-muted">Состав недоступен для этой дисциплины</div>
              </div>
            </div>

            <!-- Баннер -->
            <div class="tf-card">
              <div class="tf-card__head">
                <span class="tf-card__icon"><i class="fa fa-picture-o"></i></span>
                <div class="tf-card__titles">
                  <h3 class="tf-card__title">Баннер команды</h3>
                  <p class="tf-card__sub">Показывается на публичной странице команды</p>
                </div>
              </div>
              <div class="tf-card__body">
                <div v-if="!bannerPreview && !team.banner"
                     class="ui-drop" :class="{ 'is-over': isDragover }"
                     @dragenter.prevent="isDragover = true"
                     @dragleave.prevent="isDragover = false"
                     @dragover.prevent
                     @drop.prevent="handleFileDrop"
                     @click="$refs.fileInput.click()">
                  <input ref="fileInput" type="file" accept="image/*" style="display: none;"
                         @change="handleFileSelect">
                  <i class="fa fa-cloud-upload fa-2x"></i>
                  <span>{{ isDragover ? 'Отпустите изображение' : 'Перетащите изображение или нажмите, чтобы выбрать' }}</span>
                </div>

                <div v-else-if="bannerPreview" class="ui-banner">
                  <img :src="bannerPreview" class="ui-banner__img"/>
                  <div class="ui-banner__actions">
                    <button type="button" class="tf-btn-primary" @click="uploadBanner">
                      <i class="fa fa-check"></i> Загрузить
                    </button>
                    <button type="button" class="tf-btn-ghost" @click="cancelUpload">Отмена</button>
                  </div>
                </div>

                <div v-else class="ui-banner">
                  <img :src="team.banner.original_url" class="ui-banner__img"/>
                  <div class="ui-banner__actions">
                    <button type="button" class="tf-btn-ghost ui-btn-danger" @click="deleteBanner">
                      <i class="fa fa-trash-o"></i> Удалить баннер
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <!-- ============ ТУРНИРЫ ============ -->
          <section v-show="activeTab === 'tournaments'" class="tf-sec">
            <div class="tf-card">
              <div class="tf-card__head">
                <span class="tf-card__icon"><i class="fa fa-trophy"></i></span>
                <div class="tf-card__titles">
                  <h3 class="tf-card__title">Турниры команды</h3>
                  <p class="tf-card__sub">Заявки и составы по стадиям</p>
                </div>
              </div>
              <div class="tf-card__body ui-flush">
                <b-table
                    class="ui-table-bv"
                    responsive borderless hover show-empty
                    :fields="tournamentLogsFields"
                    :items="teamTournamentLogs"
                    empty-text="Турниров нет">
                  <template #cell(name)="data">
                    {{ data.item.tournament.name }}
                  </template>
                  <template #cell(format)="data">
                    {{ data.item.tournament.format.name }}
                  </template>
                  <template #cell(details)="data">
                    <button type="button" class="ui-act" @click="showTournDetails(data)"
                            v-b-tooltip.hover.top="'Составы по стадиям'">
                      <i class="fa" :class="data.detailsShowing ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                    </button>
                  </template>
                  <template #row-details="tournamentData">
                    <div v-if="tournamentData.item && tournamentData.item.stage_team
                               && tournamentData.item.stage_team.tournament_team
                               && tournamentData.item.stage_team.tournament_team.stage_teams"
                         class="ui-subtable">
                      <b-table :items="tournamentData.item.stage_team.tournament_team.stage_teams"
                               :fields="tournamentLogFields" borderless responsive show-empty
                               class="ui-table-bv">
                        <template #cell(stage)="data">
                          {{ data.item && data.item.stage ? data.item.stage.name : '—' }}
                        </template>
                        <template #cell(members)="data">
                          <div v-for="member in data.item.stage_team_members" :key="'memberId' + member.id">
                            <div v-for="profile in (member.user && member.user.game_profiles) || []" :key="profile.id">
                              <span v-if="profile.fields && profile.fields.nick">{{ profile.fields.nick }}</span>
                            </div>
                          </div>
                        </template>
                        <template #cell(game_id)="data">
                          <div v-for="member in data.item.stage_team_members" :key="'gameId' + member.id">
                            {{ (getPubgId(member.user) || getMlbbId(member.user)) || '—' }}
                          </div>
                        </template>
                      </b-table>
                    </div>
                    <div v-else class="ui-muted text-center py-3">Информация отсутствует</div>
                  </template>
                  <template #cell(date)="data">
                    {{ formatDateTime(data.item.tournament.start_time) }}
                  </template>
                </b-table>
              </div>

              <div class="ui-foot">
                <b-pagination class="ui-pagination my-0" v-model="teamTournamentLogsCurrentPage"
                              :total-rows="totalPagesOfTeamTournamentLogs" :per-page="10" :limit="7"
                              @change="handleTournamentLogsPageChange"/>
              </div>
            </div>
          </section>

          <!-- ============ ЛОГИ ============ -->
          <section v-show="activeTab === 'logs'" class="tf-sec">
            <div class="tf-card">
              <div class="tf-card__head">
                <span class="tf-card__icon"><i class="fa fa-history"></i></span>
                <div class="tf-card__titles">
                  <h3 class="tf-card__title">Логи команды</h3>
                  <p class="tf-card__sub">Что происходило с составом и заявками</p>
                </div>
              </div>
              <div class="tf-card__body ui-flush">
                <b-table
                    class="ui-table-bv"
                    responsive borderless hover show-empty
                    :fields="fieldsLogos"
                    :items="teamLogs"
                    empty-text="Логов нет">
                  <template #cell(date)="data">
                    {{ formatDateTime(data.item.created_at) }}
                  </template>
                  <template #cell(type)="data">
                    <span class="ui-tag">{{ data.item.type.code }}</span>
                  </template>
                  <template #cell(reason)="data">
                    <div class="ui-log">
                      <div>
                        {{ getCodeStateMessage(data.item.type.code, data.item.data).description }}
                        <template v-if="data.item.tournament">{{ data.item.tournament.name }}</template>
                        <span v-if="data.item.type.code !== 'name-changed' && data.item.data">
                          {{ data.item.data.new_name }}
                        </span>
                        <template v-if="data.item.user">{{ data.item.user.username }}</template>
                      </div>
                      <div v-if="data.item.data && data.item.data.tournament">
                        Турнир: {{ data.item.data.tournament.name }}
                      </div>
                      <div v-if="data.item.data && data.item.data.stage">
                        Этап: {{ data.item.data.stage.name }}
                      </div>
                      <div v-if="data.item.data && data.item.data.old_stage">
                        Из этапа: {{ data.item.data.old_stage.name }}
                      </div>
                      <div v-if="data.item.data && data.item.data.new_stage">
                        В этап: {{ data.item.data.new_stage.name }}
                      </div>
                      <div v-if="data.item.data && data.item.data.group">
                        Группа: {{ data.item.data.group.name }}
                      </div>
                      <div v-if="data.item.data && data.item.data.reject_description">
                        Причина: {{ data.item.data.reject_description }}
                      </div>
                      <div v-if="data.item.type.code === 'role-assigned'">
                        {{ getRoleStateMessage(data.item.data.old_role, data.item.data.role).role }}
                      </div>
                    </div>
                  </template>
                  <template #cell(who)="data">
                    {{ (data.item.author && data.item.author.username) || '—' }}
                  </template>
                </b-table>
              </div>

              <div class="ui-foot">
                <b-pagination class="ui-pagination my-0" v-model="teamLogsCurrentPage"
                              :total-rows="teamLogsTotal" :per-page="10" :limit="7"
                              @change="logsHandlePageChange"/>
              </div>
            </div>
          </section>

          <!-- ============ РЕЙТИНГ ============ -->
          <section v-show="activeTab === 'rating'" class="tf-sec">
            <div class="tf-card">
              <div class="tf-card__head">
                <span class="tf-card__icon"><i class="fa fa-star-o"></i></span>
                <div class="tf-card__titles">
                  <h3 class="tf-card__title">Начисления рейтинга</h3>
                  <p class="tf-card__sub">Правки сохраняются сразу после изменения поля</p>
                </div>
                <button type="button" class="tf-btn-primary" @click.prevent="show = true">
                  <i class="fa fa-plus"></i> Добавить
                </button>
              </div>
              <div class="tf-card__body ui-flush">
                <b-table class="ui-table-bv" :fields="fieldRating" :items="getTeamCurrentRating"
                         responsive borderless hover show-empty empty-text="Начислений нет">
                  <template #cell(index)="data">
                    {{ data.index + 1 }}
                  </template>
                  <template #cell(points)="{item}">
                    <b-form-input type="number" v-model="item.points" class="ui-input-sm"
                                  @change="updateCurrentTeamRating(item.points, item.id, item.description)"/>
                  </template>
                  <template #cell(description)="{item}">
                    <b-form-textarea v-model="item.description" rows="2"
                                     @change="updateCurrentTeamRating(item.points, item.id, item.description)"/>
                  </template>
                  <template #cell(created_at)="{item}">
                    {{ formatDateTime(item.created_at) }}
                  </template>
                </b-table>
              </div>
            </div>
          </section>
        </div>

        <!-- ======================= сводка ======================= -->
        <aside class="ui-side">
          <div class="ui-profile">
            <button type="button" class="ui-profile__logo" @click="handleOpenEditAvatar"
                    v-b-tooltip.hover.top="'Логотип команды'">
              <img :src="logoUrl" alt="" @error="event => event.target.src = TeamImagePug"/>
              <span class="ui-profile__logo-edit"><i class="fa fa-camera"></i></span>
            </button>

            <div class="ui-profile__name">{{ team.name || '—' }}</div>
            <div class="ui-profile__id">ID {{ team.id || '—' }}</div>

            <div class="ui-profile__badges">
              <span v-if="isProDivision" class="tf-badge tf-badge--warn"><i class="fa fa-star"></i> PRO</span>
              <span v-if="bans.length" class="tf-badge tf-badge--danger"><i class="fa fa-ban"></i> В бане</span>
              <span v-if="team.deleted_at" class="tf-badge tf-badge--danger">Удалена</span>
              <span v-if="!isProDivision && !bans.length && !team.deleted_at" class="tf-badge tf-badge--ok">
                <i class="fa fa-check"></i> Активна
              </span>
            </div>

            <dl class="ui-dl ui-dl--stack">
              <div>
                <dt>Дисциплина</dt>
                <dd>{{ (team.game && team.game.name) || '—' }}</dd>
              </div>
              <div>
                <dt>Дивизион</dt>
                <dd>{{ (team.division && team.division.name) || '—' }}</dd>
              </div>
              <div>
                <dt>Рейтинг</dt>
                <dd>{{ team.rating !== null && team.rating !== undefined ? team.rating : '—' }}</dd>
              </div>
              <div>
                <dt>Участников</dt>
                <dd>{{ (members || []).length }}</dd>
              </div>
              <div>
                <dt>Турниров</dt>
                <dd>{{ team.tournaments_count || 0 }}</dd>
              </div>
            </dl>

            <div class="ui-profile__actions">
              <button type="button" class="tf-btn-ghost" @click="openRenameModal">
                <i class="fa fa-pencil"></i> Переименовать
              </button>
              <router-link :to="{ name: 'teams_add_player', params: { id: $route.params.id } }" class="tf-btn-ghost">
                <i class="fa fa-user-plus"></i> Добавить игрока
              </router-link>
              <button type="button" class="tf-btn-ghost ui-btn-danger" @click="openDeleteTeamModal">
                <i class="fa fa-trash-o"></i> Удалить команду
              </button>
            </div>
          </div>
        </aside>
      </div>
    </div>
  </div>
</template>

<script>
import {TeamsAdapterMixin} from "@/mixins/Teams/TeamsAdapterMixin";
import {UsersAdapterMixin} from "@/mixins/Users/UsersAdapterMixin";
import {ROLES} from '@/constants/TeamMember/roles';
import {CODES} from '@/constants/TeamMember/logCodes';
import {PaginationHelperMixin} from "@/mixins/Helpers/PaginationHelperMixin";
import moment from 'moment'
import MlbbPlayerTable from "@/components/TeamInfo/MlbbPlayerTable.vue";
import PubgPlayerTable from "@/components/TeamInfo/PubgPlayerTable.vue";
import TeamImagePug from '@/assets/images/pugs/team.png'
import api from "@/api/api";

export default {
  name: 'TeamInfo',
  components: {MlbbPlayerTable, PubgPlayerTable},
  mixins: [TeamsAdapterMixin, UsersAdapterMixin, PaginationHelperMixin],

  created() {
    (async () => {
      try {
        // Загружаем команды и другие данные
        await Promise.all([
          this.showTeams(this.$route.params.id),
          this.showTeamRating(this.$route.params.id),
          this.shortTeamRating(this.$route.params.id),
          this.recordPageToStorage(this.teamTournamentLogsCurrentPage, this.teamTournamentLogsPageKey),
          api.teams.teamTournamentLogs({
            teamId: this.$route.params.id,
            page: this.getCurrentPageNumber(this.teamTournamentLogsPageKey),
          }),
          this.recordPageToStorage(this.teamLogsCurrentPage, this.pageKeyLogs),
          this.teamLogsShow({
            teamId: this.$route.params.id,
            pageNumber: this.getCurrentPageNumber(this.pageKeyLogs),
          })
        ]);

        // Устанавливаем турниры
        const tournamentLogs = (await api.teams.teamTournamentLogs({
          teamId: this.$route.params.id,
          page: this.teamTournamentLogsCurrentPage
        })).data;
        this.teamTournamentLogs = tournamentLogs.entry_logs || [];
        this.totalPagesOfTeamTournamentLogs = tournamentLogs.pagination.total;

        // Проверяем division.id после загрузки данных
        if (this.teamsShow && this.teamsShow.division) {
          this.isProDivision = this.teamsShow.division.id === 5;
        } else {
          this.isProDivision = false; // Убедимся, что есть fallback
        }

        // Загружаем список регионов
        this.proTeamsRegionsList = await this.fetchProTeamRegions();
        this.regionsOptions = [
          {text: 'Выберите регион', value: null, disabled: true}, // Значение по умолчанию
          ...this.proTeamsRegionsList.map((region) => ({
            text: region.name,
            value: region.id
          }))
        ];

        // Проверяем, привязан ли регион
        if (this.teamsShow.hasOwnProperty('proTeamRegionRating')) {
          this.selectedProRegionId = this.teamsShow.proTeamRegionRating.proTeamRatingRegion.id;
        }
      } catch (error) {
        console.error('Ошибка при загрузке данных:', error);
      }
    })();
  },
  data() {
    return {
      TeamImagePug,
      moment,
      pageKeyLogs: 'pageNumberTeamLogs',
      teamTournamentLogsPageKey: 'teamTournamentLogsPageNumber',
      teamTournamentLogs: [],
      totalPagesOfTeamTournamentLogs: 1,
      teamTournamentLogsCurrentPage: 1,
      fields: [
        {key: 'id', label: '№',},
        {key: 'nick', label: 'Nick',},
        {key: 'pubg_nick', label: 'Pubg nick'},
        {key: 'pubg_id', label: 'Pubg id',},
        {key: 'status', label: 'Cтатус',},
        {key: 'country', label: 'Страна',},
        {key: 'socials', label: 'Социальные сети',},
        {key: 'role', label: 'Роль',},
        {key: 'inspect', label: 'Просмотр',},
      ],
      tournamentLogsFields: [
        {key: 'id', label: '№',},
        {key: 'name', label: 'Название',},
        {key: 'format', label: 'Формат'},
        {key: 'details', label: 'Детали',},
        {key: 'date', label: 'Дата турнира',},
      ],
      fieldsTournaments: [
        {key: 'id', label: 'ID', sortable: true},
        {key: 'name', label: 'Название', sortable: true},
        {key: 'date', label: 'Дата', sortable: true},
        {key: 'format', label: 'Формат', sortable: false},
      ],
      fieldsLogos: [
        {key: 'id', label: '№',},
        {key: 'date', label: 'Дата и время',},
        {key: 'type', label: 'Тип'},
        {key: 'reason', label: 'Описание',},
        {key: 'who', label: 'Кто',},
      ],
      fieldRating: [
        {key: 'index', label: '#'},
        {key: 'points', label: 'Рейтинг'},
        {key: 'description', label: 'Описание'},
        {key: 'created_at', label: 'Дата создания'},
        {key: 'id', label: 'ID'}
      ],
      tournamentLogFields: [
        {key: 'id', label: 'id', sortable: true},
        {key: 'stage', label: 'Стадия', sortable: true},
        {key: 'members', label: 'Состав', sortable: true},
        {key: 'game_id', label: 'Game ID', sortable: false},
      ],
      striped: true,
      bordered: true,
      totalRows: 10,
      show: false,
      isProDivision: false,
      isLoadingToggleProDivision: false,
      // Banner upload
      isDragover: false,
      bannerFile: null,
      bannerPreview: null,
      proTeamsRegionsList: [],
      regionsOptions: [],
      selectedProRegionId: null,
      activeTab: 'main',
      stickyFrame: null,

      newTeamName: '',
      isShowRenameModal: false,
      isRenaming: false,
      renameError: null,

      isShowDeleteTeamModal: false,
      isDeletingTeam: false,
      deleteTeamError: null,
    }
  },
  computed: {
    /** Стор кладёт в showTeams пустой массив до загрузки — приводим к объекту. */
    team() {
      const raw = this.teamsShow;
      return raw && !Array.isArray(raw) ? raw : {};
    },

    tabs() {
      return [
        {key: 'main', label: 'Общее', icon: 'fa-shield', count: null},
        {key: 'tournaments', label: 'Турниры', icon: 'fa-trophy', count: this.totalPagesOfTeamTournamentLogs || null},
        {key: 'logs', label: 'Логи', icon: 'fa-history', count: null},
        {key: 'rating', label: 'Рейтинг', icon: 'fa-star-o', count: (this.getTeamCurrentRating || []).length || null},
      ];
    },

    logoUrl() {
      return (this.team.logo && this.team.logo.original_url) || TeamImagePug;
    },

    hasLogo() {
      return Boolean(this.team.logo && this.team.logo.original_url);
    },

    bans() {
      return this.team.currentBans || [];
    },

    owner() {
      const list = this.members || [];
      const found = list.find(member => member.role_in_team && member.role_in_team.code === 'owner');
      return found ? (found.username || found.email) : '';
    },

    isRenameValid() {
      return typeof this.newTeamName === 'string' && this.newTeamName.trim().length >= 2;
    },
  },
  mounted() {
    this.updateStickyOffsets();
    window.addEventListener('resize', this.updateStickyOffsets);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateStickyOffsets);
    if (this.stickyFrame) cancelAnimationFrame(this.stickyFrame);
  },
  watch: {
    selectedProRegionId(newRegionId, oldRegionId) {

      if (newRegionId && !this.teamsShow.hasOwnProperty('proTeamRegionRating')) {
        this.attachProTeamRegion(this.teamsShow.id, newRegionId);
      }
    }
  },
  methods: {
    /** Шапка приложения фиксированная и её высота зависит от темы — считаем оффсет по факту. */
    updateStickyOffsets() {
      if (this.stickyFrame) return;
      this.stickyFrame = requestAnimationFrame(() => {
        this.stickyFrame = null;
        if (!this.$el || !this.$el.style) return;
        const appHeader = document.querySelector('.page-header');
        const headerHeight = appHeader ? Math.round(appHeader.getBoundingClientRect().height) : 0;
        this.$el.style.setProperty('--tf-sticky-top', headerHeight + 'px');
      });
    },

    goTab(key) {
      this.activeTab = key;
      this.$nextTick(() => {
        const anchor = this.$refs.tabsTop;
        if (!anchor || !anchor.getBoundingClientRect) return;
        const appHeader = document.querySelector('.page-header');
        const headerHeight = appHeader ? Math.round(appHeader.getBoundingClientRect().height) : 0;
        const top = anchor.getBoundingClientRect().top + window.pageYOffset - headerHeight - 76;
        window.scrollTo({top: Math.max(top, 0), behavior: 'smooth'});
      });
    },

    goBack() {
      this.$router.push({name: 'teams'});
    },

    copyValue(value, label) {
      if (value === null || value === undefined || value === '') return;
      const text = String(value);
      const done = () => this.$bvToast.toast(`${label} скопирован`, {title: 'Готово', variant: 'success', solid: true});
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(done).catch(() => {
        });
        return;
      }
      const area = document.createElement('textarea');
      area.value = text;
      document.body.appendChild(area);
      area.select();
      try {
        document.execCommand('copy');
        done();
      } finally {
        document.body.removeChild(area);
      }
    },

    openRenameModal() {
      this.newTeamName = this.team.name || '';
      this.renameError = null;
      this.isShowRenameModal = true;
    },

    /** Название правим в модалке: раньше поле писало прямо в стор и шапка менялась до сохранения. */
    async confirmRename() {
      if (!this.isRenameValid || this.isRenaming) return;
      this.isRenaming = true;
      this.renameError = null;
      try {
        await this.updateTeamName({teamId: this.$route.params.id, name: this.newTeamName.trim()});
        await this.showTeams(this.$route.params.id);
        this.isShowRenameModal = false;
      } catch (error) {
        this.renameError = (error && error.response && error.response.data && error.response.data.message)
            || 'Не удалось переименовать команду';
      } finally {
        this.isRenaming = false;
      }
    },

    formatDateTime(value) {
      if (!value) return '—';
      const date = new Date(String(value).replace(' ', 'T'));
      if (isNaN(date.getTime())) return '—';
      const pad = (n) => String(n).padStart(2, '0');
      return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
    },

    handleOpenEditAvatar() {
      this.$bvModal.show('modal-edit-avatar')
    },
    getPubgId(member) {
      return member.game_profiles
          ?.find(p => p.fields?.pubg_id)
          ?.fields?.pubg_id;
    },
    async handleDownloadTeamAvatar(imgUrl, name) {
      try {

        const link = document.createElement('a')
        link.href = imgUrl
        link.download = name
        link.target = '_blank'
        document.body.appendChild(link);
        link.click()
        document.body.removeChild(link);

        this.$bvToast.toast('Логотип успешно скачен!', {
          title: 'Успех',
          variant: 'success',
          solid: true
        });
      } catch (error) {
        console.error('Ошибка при скачивании логотипа:', error);
        this.$bvToast.toast('Не удалось скачать логотип', {
          title: 'Ошибка',
          variant: 'danger',
          solid: true
        });

      }
    },
    getMlbbId(member) {
      return member.game_profiles
          ?.find(p => p.fields?.mlbb_id)
          ?.fields?.mlbb_id;
    },
    handleLogoClick() {
      // Открываем окно выбора файла
      this.$refs.logoInput.click();
    },
    handleLogoSelect(event) {
      const file = event.target.files[0];
      // Проверяем, что файл есть и что это изображение
      if (file && file.type.startsWith('image/')) {
        this.uploadLogo(file);
      }
      this.$bvModal.hide('modal-edit-avatar');
    },
    async deleteLogo() {
      try {
        await api.teams.removeTeamLogo(this.$route.params.id);
        await this.showTeams(this.$route.params.id);

        this.$bvToast.toast('Логотип успешно удалён!', {
          title: 'Успех',
          variant: 'success',
          solid: true
        });
      } catch (error) {
        console.error('Ошибка при удалении логотипа:', error);
        this.$bvToast.toast('Не удалось удалить логотип', {
          title: 'Ошибка',
          variant: 'danger',
          solid: true
        });
      }
    },
    async uploadLogo(file) {
      const formData = new FormData();
      formData.append('logo', file);
      try {
        await api.teams.uploadTeamLogo(this.$route.params.id, formData);
        // Обновляем данные команды, чтобы сразу увидеть новое лого
        await this.showTeams(this.$route.params.id);
        this.$bvToast.toast('Логотип успешно загружен!', {
          title: 'Успех',
          variant: 'success',
          solid: true
        });
      } catch (error) {
        if (error.response.status === 413) {
          this.$bvToast.toast('Слишком большой размер файла', {
            title: 'Ошибка',
            variant: 'danger',
            solid: true
          });
          return;
        }
        this.$bvToast.toast('Не удалось загрузить логотип', {
          title: 'Ошибка',
          variant: 'danger',
          solid: true
        });
      } finally {
        this.$refs.logoInput.value = '';
      }
    },
    acceptTeamRating() {
      this.addTeamRating({
        team_id: this.$route.params.id,
        points: this.newRating,
        description: this.newDescription
      }).then(() => {
        this.show = false
        this.shortTeamRating(this.$route.params.id)
      })
    },
    updateCurrentTeamRating(points, id, description) {
      this.updateTeamRating({points: points, id: id, team_id: this.$route.params.id, description: description})
    },
    getRoleStateMessage(old_role, role) {
      return {role: `Был ${ROLES[old_role]} - Стал ${ROLES[role]}`}
    },
    getCodeStateMessage(code, data = null) {
      if (code === "name-changed" && data?.old_name && data?.new_name) {
        return {
          description: `Изменено название команды ${data.old_name ?? ""} на ${data.new_name ?? ""}`
        }
      }
      return {description: `${CODES[code]}`};
    },
    trClass: rowData => rowData?.currentBans?.length && 'table-danger',
    logsHandlePageChange(pageNumber) {
      this.recordPageToStorage(pageNumber, this.pageKeyLogs)
      this.teamLogsShow({
        teamId: this.$route.params.id,
        pageNumber: pageNumber,
      })
    },
    async handleTournamentLogsPageChange(pageNumber) {
      const params = {page: pageNumber, teamId: this.$route.params.id}
      this.recordPageToStorage(pageNumber, this.teamTournamentLogsPageKey)
      const response = await api.teams.teamTournamentLogs(params)
      this.teamTournamentLogs = response.data.entry_logs || []
    },
    showTournDetails(data) {
      data.toggleDetails();
    },
    async toggleProDivision() {
      this.isLoadingToggleProDivision = true; // Устанавливаем состояние загрузки
      try {
        await (this.isProDivision
            ? api.teams.bindProDivision
            : api.teams.unbindProDivision)(this.$route.params.id);

        if (!this.isProDivision && this.selectedProRegionId) {
          await this.detachProTeamRegion(this.teamsShow.id);
        }
      } catch (error) {
        console.error('Error toggling pro division:', error);
        this.isProDivision = !this.isProDivision; // Возвращаем предыдущее состояние при ошибке
        this.$bvToast.toast('Не удалось изменить статус Pro Division', {
          title: 'Ошибка',
          variant: 'danger',
          solid: true,
        });
      } finally {
        this.isLoadingToggleProDivision = false; // Сбрасываем состояние загрузки
      }
    },
    handleFileSelect(event) {
      const file = event.target.files[0];
      this.processFile(file);
    },

    handleFileDrop(event) {
      this.isDragover = false;
      const file = event.dataTransfer.files[0];
      this.processFile(file);
    },
    processFile(file) {
      if (file && file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          this.bannerPreview = e.target.result;
          this.bannerFile = file;
        };
        reader.readAsDataURL(file);
      }
    },
    async uploadBanner() {
      if (!this.bannerFile) return;

      const formData = new FormData();
      formData.append('file', this.bannerFile);
      formData.append('_method', 'patch');

      try {
        await api.teams.uploadBanner(this.$route.params.id, formData);
        // Refresh the team data to get the new banner
        await this.showTeams(this.$route.params.id);
        this.bannerPreview = null;
        this.bannerFile = null;
        this.$bvToast.toast('Баннер успешно загружен', {
          title: 'Успех',
          variant: 'success',
          solid: true
        });
      } catch (error) {
        console.error('Error uploading banner:', error);
        this.$bvToast.toast('Не удалось загрузить баннер', {
          title: 'Ошибка',
          variant: 'danger',
          solid: true
        });
      }
    },
    cancelUpload() {
      this.bannerPreview = null;
      this.bannerFile = null;
    },
    async deleteBanner() {
      try {
        await api.teams.deleteBanner(this.$route.params.id);
        // Refresh the team data to remove the banner
        await this.showTeams(this.$route.params.id);
        this.$bvToast.toast('Баннер успешно удален', {
          title: 'Успех',
          variant: 'success',
          solid: true
        });
      } catch (error) {
        console.error('Error deleting banner:', error);
        this.$bvToast.toast('Не удалось удалить баннер', {
          title: 'Ошибка',
          variant: 'danger',
          solid: true
        });
      }
    },
    async fetchProTeamRegions() {
      try {
        const response = await api.teams.proTeamRegions();
        if (response.status === 200) {
          return response.data.data;
        } else {
          console.error(`Error: Received status ${response.status}`);
          return [];
        }
      } catch (error) {
        console.error('Error fetching pro team regions:', error);
        return [];
      }
    },
    async detachProTeamRegion(teamId) {
      try {
        const response = await api.teams.detachProTeamRegion(teamId);
        if (response.status === 204) {
          this.clearRegion()
          this.$bvToast.toast('Регион успешно отвязан', {
            title: 'Успех',
            variant: 'success',
            solid: true
          });
        } else {
          console.error(`Ошибка: получен статус ${response.status}`);
          this.$bvToast.toast('Не удалось отвязать регион', {
            title: 'Ошибка',
            variant: 'danger',
            solid: true
          });
        }
      } catch (error) {
        console.error('Ошибка отвязки региона:', error);
        this.$bvToast.toast('Не удалось отвязать регион', {
          title: 'Ошибка',
          variant: 'danger',
          solid: true
        });
      }
    },
    async attachProTeamRegion(teamId, regionId) {
      try {
        const response = await api.teams.attachProTeamRegion(teamId, regionId);
        if (response.status === 204) {
          this.$bvToast.toast('Регион успешно привязан', {
            title: 'Успех',
            variant: 'success',
            solid: true
          });
        } else {
          this.$bvToast.toast('Не удалось привязать регион', {
            title: 'Ошибка',
            variant: 'danger',
            solid: true
          });
        }
      } catch (error) {
        console.error('Ошибка привязки региона:', error);
        this.$bvToast.toast('Не удалось привязать регион', {
          title: 'Ошибка',
          variant: 'danger',
          solid: true
        });
      }
    },
    clearRegion() {
      this.selectedProRegionId = null;
    },
    openDeleteTeamModal() {
      this.deleteTeamError = null;
      this.isShowDeleteTeamModal = true;
    },
    async confirmDeleteTeam() {
      this.isDeletingTeam = true;
      this.deleteTeamError = null;
      try {
        await api.teams.deleteTeam(this.$route.params.id);
        this.isShowDeleteTeamModal = false;
        this.$bvToast.toast('Команда успешно удалена', {
          title: 'Успех',
          variant: 'success',
          solid: true
        });
        this.$router.push({name: 'teams'});
      } catch (error) {
        this.deleteTeamError = error?.response?.data?.message || 'Не удалось удалить команду';
      } finally {
        this.isDeletingTeam = false;
      }
    },
  },
}
</script>
<style scoped lang="scss">
@import "~@/assets/scss/tournament-form.scss";

/* ---------- горизонтальные табы ---------- */
.ui-tabs {
  position: sticky;
  top: calc(var(--tf-sticky-top, 0px) + 66px);
  z-index: 8;
  display: flex;
  gap: 4px;
  overflow-x: auto;
  padding: 6px;
  margin-bottom: 16px;
  background: var(--tf-bg);
  border: 1px solid var(--tf-border);
  border-radius: var(--tf-radius);
  box-shadow: var(--tf-shadow);
}

.ui-tab {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
  height: 36px;
  padding: 0 14px;
  border: 0;
  border-radius: 10px;
  background: transparent;
  color: var(--tf-text);
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
  cursor: pointer;

  > i:first-child { color: var(--tf-muted); font-size: 13px; }

  &:hover { background: var(--tf-soft); }

  &.is-on {
    background: var(--tf-primary-soft);
    color: var(--tf-primary);

    > i:first-child { color: var(--tf-primary); }
  }
}

.ui-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 19px;
  height: 19px;
  padding: 0 5px;
  border-radius: 999px;
  background: var(--tf-soft-2);
  color: var(--tf-muted);
  font-size: 10.5px;
  font-weight: 700;
}

.ui-tab.is-on .ui-count { background: var(--tf-primary); color: #fff; }

/* ---------- разметка ---------- */
.ui-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 320px;
  gap: 16px;
  align-items: start;
}

.ui-main { min-width: 0; }

.ui-side { position: sticky; top: calc(var(--tf-sticky-top, 0px) + 118px); }

@media (max-width: 1199px) {
  .ui-layout { grid-template-columns: minmax(0, 1fr); }
  .ui-side { position: static; order: -1; }
}

/* ---------- карточка команды ---------- */
.ui-profile {
  background: var(--tf-bg);
  border: 1px solid var(--tf-border);
  border-radius: var(--tf-radius);
  box-shadow: var(--tf-shadow);
  padding: 20px 18px;
  text-align: center;

  &__logo {
    position: relative;
    width: 96px;
    height: 96px;
    padding: 0;
    border: 0;
    border-radius: 28px;
    background: var(--tf-soft);
    overflow: hidden;
    cursor: pointer;

    img { width: 100%; height: 100%; object-fit: cover; }

    &-edit {
      position: absolute;
      right: 0;
      bottom: 0;
      width: 28px;
      height: 28px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 10px 0 0 0;
      background: rgba(0, 0, 0, .55);
      color: #fff;
      font-size: 12px;
      opacity: 0;
      transition: opacity .16s ease;
    }

    &:hover &-edit { opacity: 1; }
  }

  &__name {
    margin-top: 12px;
    font-size: 16px;
    font-weight: 600;
    color: var(--tf-text);
    word-break: break-word;
  }

  &__id { margin-top: 2px; font-size: 12px; color: var(--tf-muted); }

  &__badges {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 6px;
    margin-top: 12px;
  }

  &__actions {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 16px;

    .tf-btn-ghost, .tf-btn-primary { justify-content: center; }
  }
}

.ui-logo-modal {
  display: flex;
  justify-content: center;

  &__img {
    width: 160px;
    height: 160px;
    border-radius: 32px;
    object-fit: cover;
    background: var(--tf-soft);
  }
}

/* ---------- список «ключ — значение» ---------- */
.ui-dl {
  margin: 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 0 24px;

  > div {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 16px;
    padding: 10px 0;
    border-bottom: 1px dashed var(--tf-border);
  }

  dt {
    margin: 0;
    font-size: 12px;
    font-weight: 500;
    color: var(--tf-muted);
    white-space: nowrap;
  }

  dd {
    margin: 0;
    font-size: 13px;
    text-align: right;
    color: var(--tf-text);
    word-break: break-word;
  }

  &--stack {
    grid-template-columns: minmax(0, 1fr);
    margin-top: 14px;
    text-align: left;

    > div:last-child { border-bottom: 0; }
  }
}

.ui-copy {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 0;
  background: none;
  padding: 0;
  color: inherit;
  font-size: inherit;
  cursor: pointer;

  i { opacity: 0; font-size: 10px; transition: opacity .14s ease; }

  &:hover {
    color: var(--tf-primary);

    i { opacity: 1; }
  }
}

.ui-muted { color: var(--tf-muted); font-size: 13px; }

/* ---------- бан ---------- */
.ui-ban {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 8px;
  margin-top: 6px;
  font-size: 12.5px;
  color: var(--tf-text);

  b { color: var(--tf-danger); }

  em { font-style: normal; color: var(--tf-muted); }
}

/* ---------- регион pro-дивизиона ---------- */
.ui-region {
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px solid var(--tf-border);

  &__row {
    display: flex;
    gap: 8px;
    align-items: center;
    flex-wrap: wrap;

    ::v-deep .custom-select { max-width: 280px; }
  }
}

/* ---------- кнопки-опасность ---------- */
.ui-btn-danger {
  border-color: var(--tf-danger);
  color: var(--tf-danger) !important;

  &:hover { background: var(--tf-danger-soft); border-color: var(--tf-danger); color: var(--tf-danger) !important; }
}

.ui-btn-danger-solid {
  background: var(--tf-danger);

  &:hover { filter: brightness(1.06); }
}

.ui-icon-danger:hover {
  border-color: var(--tf-danger);
  color: var(--tf-danger);
}

/* ---------- баннер ---------- */
.ui-drop {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 160px;
  padding: 20px;
  border: 1px dashed var(--tf-border);
  border-radius: 12px;
  background: var(--tf-soft);
  color: var(--tf-muted);
  font-size: 13px;
  text-align: center;
  cursor: pointer;
  transition: border-color .16s ease, background .16s ease;

  &:hover { border-color: var(--tf-primary); }

  &.is-over {
    border-color: var(--tf-primary);
    background: var(--tf-primary-soft);
    color: var(--tf-primary);
  }
}

.ui-banner {
  &__img {
    display: block;
    width: 100%;
    max-height: 260px;
    object-fit: cover;
    border-radius: 12px;
    background: var(--tf-soft-2);
  }

  &__actions {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    margin-top: 12px;
  }
}

/* ---------- таблицы bootstrap-vue внутри карточек ---------- */
.ui-flush { padding: 0; }

.ui-table-bv ::v-deep {
  margin: 0;
  color: var(--tf-text);

  thead th {
    background: var(--tf-bg);
    border: 0 !important;
    border-bottom: 1px solid var(--tf-border) !important;
    padding: 11px 16px;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: .05em;
    color: var(--tf-muted);
    white-space: nowrap;
  }

  tbody td {
    border: 0 !important;
    border-bottom: 1px solid var(--tf-border) !important;
    padding: 12px 16px;
    font-size: 13px;
    vertical-align: middle;
  }

  tbody tr:last-child td { border-bottom: 0 !important; }

  tbody tr:hover td { background: var(--tf-soft); }
}

.ui-subtable {
  padding: 4px 16px 16px;
  background: var(--tf-soft);
}

.ui-act {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: 1px solid var(--tf-border);
  border-radius: 9px;
  background: var(--tf-bg);
  color: var(--tf-muted);
  font-size: 12px;
  cursor: pointer;

  &:hover { border-color: var(--tf-primary); background: var(--tf-primary-soft); color: var(--tf-primary); }
}

.ui-tag {
  display: inline-flex;
  padding: 2px 9px;
  border-radius: 999px;
  background: var(--tf-primary-soft);
  color: var(--tf-primary);
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
}

.ui-log {
  font-size: 12.5px;
  line-height: 1.5;
  max-width: 560px;

  > div + div { color: var(--tf-muted); font-size: 11.5px; }
}

.ui-input-sm { max-width: 110px; }

.ui-players ::v-deep table {
  margin-bottom: 0;
}

/* ---------- подвал с пагинацией ---------- */
.ui-foot {
  display: flex;
  justify-content: flex-end;
  padding: 13px 16px;
  border-top: 1px solid var(--tf-border);
  background: var(--tf-soft);
}

.ui-pagination ::v-deep {
  .page-item .page-link {
    border: 0;
    margin: 0 2px;
    min-width: 32px;
    height: 32px;
    line-height: 32px;
    padding: 0;
    text-align: center;
    border-radius: 9px;
    color: var(--tf-muted);
    background: transparent;
    font-size: 13px;
  }

  .page-item .page-link:hover { background: var(--tf-soft-2); color: var(--tf-primary); }

  .page-item.active .page-link { background: var(--tf-primary); color: #fff !important; }

  .page-item.disabled .page-link { opacity: .4; }
}
</style>
